import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, RotateCcw, Target, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAcademyStore } from '../../store/academyStore';
import { useSfx } from '../../store/sfxStore';

export const GridInvaders = () => {
    const { play } = useSfx();
    const { completeModule } = useAcademyStore();
    
    const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameOver'>('idle');
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    
    // Grid Setup
    const COLS = 5;
    const ROWS = 5;
    
    // Player Position (grid coordinates, 1-indexed)
    const [targetPos, setTargetPos] = useState({ col: 3, row: 5 });
    
    // Bugs (array of objects { id, col, row })
    const [bugs, setBugs] = useState<{id: number, col: number, row: number}[]>([]);
    
    const requestRef = useRef<number>();
    const lastBugTime = useRef<number>(0);
    const lastMoveTime = useRef<number>(0);

    const startGame = () => {
        play('click');
        setScore(0);
        setLives(3);
        setBugs([]);
        setTargetPos({ col: 3, row: 5 });
        setGameState('playing');
        lastBugTime.current = Date.now();
        lastMoveTime.current = Date.now();
    };

    const fireTurret = () => {
        if (gameState !== 'playing') return;
        
        setBugs(prev => {
            const hitBugs = prev.filter(b => b.col === targetPos.col && b.row === targetPos.row);
            const remainingBugs = prev.filter(b => !(b.col === targetPos.col && b.row === targetPos.row));
            
            if (hitBugs.length > 0) {
                play('success');
                setScore(s => s + (hitBugs.length * 50));
            } else {
                play('click'); // Miss
            }
            
            return remainingBugs;
        });
    };

    const handleInput = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fireTurret();
    };

    useEffect(() => {
        if (gameState !== 'playing') return;

        const loop = () => {
            const now = Date.now();
            
            // Spawn new bug every 2.5 seconds (gets faster with score)
            const spawnRate = Math.max(800, 2500 - (score * 2));
            if (now - lastBugTime.current > spawnRate) {
                setBugs(prev => [
                    ...prev,
                    {
                        id: now,
                        col: Math.floor(Math.random() * COLS) + 1,
                        row: 1 
                    }
                ]);
                lastBugTime.current = now;
            }

            // Move bugs down every 1.5s
            if (now - lastMoveTime.current > 1500) {
                setBugs(prev => {
                    let lostLife = 0;
                    const moved = prev.map(b => ({ ...b, row: b.row + 1 })).filter(b => {
                        if (b.row > ROWS) {
                            lostLife++;
                            return false;
                        }
                        return true;
                    });

                    if (lostLife > 0) {
                        play('fail');
                        setLives(l => {
                            const newLives = l - lostLife;
                            if (newLives <= 0) setGameState('gameOver');
                            return newLives;
                        });
                    }
                    return moved;
                });
                lastMoveTime.current = now;
            }

            requestRef.current = requestAnimationFrame(loop);
        };

        requestRef.current = requestAnimationFrame(loop);

        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [gameState, score]);

    useEffect(() => {
        if (gameState === 'gameOver' && score > 400) {
            completeModule('grid-invaders-game', score);
        }
    }, [gameState]);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 flex flex-col pt-20">
            {/* Header */}
            <div className="bg-slate-800 border-b border-pink-500/30 p-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/games" onClick={() => play('click')} className="flex items-center gap-2 text-pink-400 font-bold uppercase tracking-widest hover:text-pink-300 transition-colors">
                        <ArrowLeft size={20} /> Base Command
                    </Link>
                    <div className="flex items-center gap-6">
                        <div className="text-xl font-bold font-mono text-pink-500">
                            SCORE: <span className="text-white drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]">{score.toString().padStart(4, '0')}</span>
                        </div>
                        <div className="flex gap-1">
                            {[...Array(3)].map((_, i) => (
                                <motion.div 
                                    key={i} 
                                    animate={{ opacity: i < lives ? 1 : 0.2 }}
                                    className="w-4 h-4 bg-pink-500 rounded-full shadow-[0_0_8px_#ec4899]" 
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Game Area */}
            <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full relative p-6">
                
                {gameState === 'idle' && (
                    <div className="flex-1 flex items-center justify-center">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-800 border-2 border-pink-500/50 p-12 text-center max-w-lg w-full rounded-3xl shadow-[0_0_30px_rgba(236,72,153,0.15)] relative overflow-hidden">
                            <ShieldAlert className="w-24 h-24 text-pink-500 mx-auto mb-6 opacity-80" />
                            <h1 className="text-4xl font-black mb-4 tracking-widest text-white drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]">CSS GRID INVADERS</h1>
                            <p className="text-pink-200 mb-8 font-medium leading-relaxed">
                                Incoming alien bugs detected! Adjust your Turret's CSS `grid-column` and `grid-row` coordinates to intercept and destroy them before they breach the Layout!
                            </p>
                            <button onClick={startGame} className="w-full py-4 bg-pink-600 hover:bg-pink-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 text-xl transition-all shadow-lg hover:shadow-pink-500/50 hover:scale-105">
                                <Play fill="currentColor" /> INITIATE DEFENSE
                            </button>
                        </motion.div>
                    </div>
                )}

                {gameState === 'playing' && (
                    <div className="flex-1 flex flex-col lg:flex-row gap-8 items-center justify-center h-full">
                        
                        {/* CSS Terminal */}
                        <form onSubmit={handleInput} className="bg-slate-950 p-6 rounded-3xl border-2 border-slate-700 w-full lg:w-1/3 flex flex-col gap-4 font-mono shadow-2xl relative overflow-hidden group">
                           <div className="absolute inset-0 bg-[linear-gradient(rgba(236,72,153,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(236,72,153,0.03)_1px,transparent_1px)] bg-[length:20px_20px] pointer-events-none" />
                           <h3 className="text-pink-400 font-bold border-b border-pink-900/50 pb-2 mb-4 tracking-widest uppercase text-sm">Turret Configuration</h3>
                           
                           <div>
                               <label className="text-slate-500 text-xs uppercase mb-1 block">.turret {'{'}</label>
                               <div className="pl-4 space-y-3">
                                   <div className="flex items-center text-sm gap-2">
                                       <span className="text-blue-300">grid-column:</span>
                                       <input 
                                           autoFocus
                                           type="number" min="1" max={COLS}
                                           value={targetPos.col}
                                           onChange={(e) => setTargetPos(prev => ({ ...prev, col: Number(e.target.value) }))}
                                           className="w-12 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-center focus:border-pink-500 outline-none" 
                                       />;
                                   </div>
                                   <div className="flex items-center text-sm gap-2">
                                       <span className="text-blue-300">grid-row:</span>
                                       <input 
                                           type="number" min="1" max={ROWS}
                                           value={targetPos.row}
                                           onChange={(e) => setTargetPos(prev => ({ ...prev, row: Number(e.target.value) }))}
                                           className="w-12 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-center focus:border-pink-500 outline-none" 
                                       />;
                                   </div>
                               </div>
                               <label className="text-slate-500 text-xs uppercase mt-3 block">{'}'}</label>
                           </div>

                           <button type="submit" className="mt-8 py-4 bg-pink-600 hover:bg-pink-500 rounded-xl font-black tracking-widest flex items-center justify-center gap-2 transition-transform active:scale-95 shadow-lg shadow-pink-600/30">
                               <Target size={20} /> FIRE
                           </button>
                        </form>

                        {/* Battlefield Grid */}
                        <div className="w-full lg:w-2/3 max-w-md aspect-square bg-slate-800 rounded-2xl border-4 border-slate-700 shadow-[inset_0_0_50px_rgba(0,0,0,0.5)] p-4 relative">
                            {/* Grid Lines Overlay */}
                            <div className="absolute inset-4 pointer-events-none" style={{
                                display: 'grid', gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS}, 1fr)`
                            }}>
                                {[...Array(COLS * ROWS)].map((_, i) => (
                                    <div key={i} className="border border-slate-700/50" />
                                ))}
                            </div>

                            {/* Active Grid */}
                            <div className="w-full h-full relative" style={{
                                display: 'grid', gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS}, 1fr)`
                            }}>
                                {/* Turret Target */}
                                <div 
                                    className="border-2 border-dashed border-pink-500 bg-pink-500/20 rounded-lg flex items-center justify-center transition-all duration-200 relative z-10"
                                    style={{ gridColumn: targetPos.col, gridRow: targetPos.row }}
                                >
                                    <Target className="text-pink-400 absolute opacity-50" size={32} />
                                </div>

                                {/* Bugs */}
                                <AnimatePresence>
                                    {bugs.map(bug => (
                                        <motion.div
                                            key={bug.id}
                                            initial={{ opacity: 0, scale: 0 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0, rotate: 180 }}
                                            transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                            className="bg-green-500 m-2 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(34,197,94,0.6)] z-20"
                                            style={{ gridColumn: bug.col, gridRow: bug.row }}
                                        >
                                            <span className="text-2xl filter brightness-0 invert">👾</span>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                )}

                {gameState === 'gameOver' && (
                    <div className="flex-1 flex items-center justify-center z-10">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-800 border-2 border-pink-500 p-12 text-center max-w-lg w-full rounded-3xl shadow-[0_0_50px_rgba(236,72,153,0.3)]">
                            <h2 className="text-4xl font-black mb-2 text-white tracking-widest drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]">GRID BREACHED</h2>
                            <p className="text-pink-400 mb-6 uppercase text-sm font-bold">The Layout is Corrupted</p>
                            
                            <div className="text-6xl font-black text-pink-500 my-8 drop-shadow-[0_0_15px_rgba(236,72,153,0.5)] font-mono">
                                {score.toString().padStart(4, '0')}
                            </div>
                            
                            <button onClick={startGame} className="w-full py-4 border border-pink-500 bg-pink-900/40 hover:bg-pink-500 hover:text-white text-pink-400 font-bold flex items-center justify-center gap-2 text-xl tracking-widest rounded-xl transition-all mb-4">
                                <RotateCcw size={20} /> RESTART DEFENSE
                            </button>
                            <Link to="/games" className="w-full py-4 text-slate-400 hover:text-white hover:bg-slate-700 font-bold flex items-center justify-center gap-2 text-lg uppercase tracking-widest rounded-xl transition-all block">
                                ABORT TO ARCADE
                            </Link>
                        </motion.div>
                    </div>
                )}

            </div>
        </div>
    );
};
